// cells.tsx — wiederverwendbare Zell-Primitiven für die Redesign-Tabellen
// (Refined Classic). Reine Optik; nutzen die in contactRedesign.css unter
// `.kvp-rd` (und `.kvp-rd-scrim`) definierten Klassen. Müssen daher innerhalb
// eines `.kvp-rd`-Wrappers gerendert werden.

import * as React from "react";
import { I } from "../kvpIcons";

/** Auswahl-Checkbox (`.cbx`). Stoppt die Row-Click-Propagation. */
export function Checkbox({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange?: () => void;
  ariaLabel?: string;
}) {
  return (
    <span
      className={"cbx" + (checked ? " on" : "")}
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        onChange?.();
      }}
    >
      {I.check}
    </span>
  );
}

/** Status-Pill (z.B. Kontaktstatus). `tone` ist die Farbklasse (z.B. "chip-kunde"). */
export function Chip({ tone, children }: { tone?: string; children: React.ReactNode }) {
  return (
    <span className={"chip" + (tone ? " " + tone : "")}>
      <span className="cd" />
      {children}
    </span>
  );
}

/** Kürzel-/Verantwortlichen-Monogramm als farbiger Kreis (`.mono` + `.mg`).
 *  `tone` = mg-* Farbklasse ODER `color` = inline {bg,fg} (z.B. Hash-Farbe). */
export function Monogram({
  label,
  tone,
  color,
  title,
}: {
  label: React.ReactNode;
  tone?: string;
  color?: { bg: string; fg: string };
  title?: string;
}) {
  return (
    <span className="mono" title={title}>
      <span
        className={"mg" + (tone ? " " + tone : "")}
        style={color ? { backgroundColor: color.bg, color: color.fg } : undefined}
      >
        {label}
      </span>
    </span>
  );
}

/** Vorgang-Badge als Kreis (`.vb`), Standard-Label „V". */
export function VorgangBadge({
  label = "V",
  title,
  className,
}: {
  label?: React.ReactNode;
  title?: string;
  className?: string;
}) {
  return (
    <span className={"vb" + (className ? " " + className : "")} title={title}>
      {label}
    </span>
  );
}

/** Gold-Sterne-Rating (fraktionale Füllung) für die K-Relevanz. */
export function StarRating({ value, max = 5 }: { value: number; max?: number }) {
  const v = Math.max(0, Math.min(max, value || 0));
  return (
    <span className="rd-stars" aria-label={`${v} von ${max}`}>
      {Array.from({ length: max }, (_, i) => {
        const fill = Math.max(0, Math.min(1, v - i));
        return (
          <span key={i} className="rd-star">
            <span className="rd-star-empty">★</span>
            <span className="rd-star-fill" style={{ width: `${fill * 100}%` }}>★</span>
          </span>
        );
      })}
    </span>
  );
}

/** Typ-Zelle: Icon + Label (z.B. Firma/Person). */
export function TypeCell({ icon, label }: { icon: React.ReactNode; label: React.ReactNode }) {
  return (
    <span className="typ">
      {icon}
      {label}
    </span>
  );
}

/** Ghost-Icon-Button für Zellen-Aktionen (Anrufen/E-Mail/Bearbeiten/…). */
export function GhostIconButton({
  title,
  icon,
  onClick,
}: {
  title?: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className="gic"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {icon}
    </button>
  );
}

/** Container für mehrere Zellen-Aktionen nebeneinander; stoppt Row-Click. */
export function IconActions({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }} onClick={(e) => e.stopPropagation()}>
      {children}
    </span>
  );
}

/** Platzhalter „—" (muted) für leere Werte. */
export const Dash = () => <span className="muted">—</span>;

/** Rendert den Wert oder ein gemutetes „—", wenn leer/leerstring. */
export function ValueOrDash({ value }: { value?: React.ReactNode }) {
  const empty = value == null || value === "" || value === "—";
  return empty ? <Dash /> : <>{value}</>;
}
